import { Attachment } from './resolution-center.service';

export type ChatObjectAttachmentType = 'lesson' | 'course' | 'document' | 'user_folder';

export interface LastMessageAttachmentPreview {
    text: string;
    icon: string | null;
}

interface LastMessageLike {
    message?: string;
    attachments?: Attachment[];
    is_my_message?: boolean;
    author?: { name: string };
}

const OBJECT_ATTACHMENT_CONFIG: Record<ChatObjectAttachmentType, { label: string; pluralLabel: string; icon: string }> = {
    lesson: {
        label: 'Lesson',
        pluralLabel: 'Lessons',
        icon: 'assets/messaging/ic_lesson_messages.svg',
    },
    course: {
        label: 'Category',
        pluralLabel: 'Categories',
        icon: 'assets/messaging/ic_course_messages.svg',
    },
    document: {
        label: 'PDF',
        pluralLabel: 'PDFs',
        icon: 'assets/messaging/ic_pdf_messages.svg',
    },
    user_folder: {
        label: 'Folder',
        pluralLabel: 'Folders',
        icon: 'assets/messaging/ic_folder_messages.svg',
    },
};

export function isFileAttachment(attachment: Attachment): boolean {
    return !attachment.object_type || attachment.object_type === 'attachment';
}

export function isObjectAttachment(attachment: Attachment): attachment is Attachment & { object_type: ChatObjectAttachmentType } {
    return !!attachment.object_type && attachment.object_type !== 'attachment';
}

export function getFileAttachmentType(attachment: Attachment): string {
    if (!attachment?.attachment) {
        return 'document';
    }

    const type = attachment.attachment.type?.toLowerCase();
    const mimeType = attachment.attachment.mime_type?.toLowerCase() || '';
    const url = attachment.attachment.url?.toLowerCase() || '';
    const name = attachment.attachment.name?.toLowerCase() || '';

    if (
        type === 'gif' ||
        mimeType === 'image/gif' ||
        url.includes('giphy.com') ||
        url.includes('i.giphy.com') ||
        url.endsWith('.gif') ||
        name.endsWith('.gif')
    ) {
        return 'GIF';
    }

    if (type === 'video' || mimeType.startsWith('video/')) {
        return 'video';
    }

    if (type === 'image' || mimeType.startsWith('image/')) {
        return 'photo';
    }

    return 'document';
}

export function getObjectAttachmentLabel(objectType: string, count = 1): string {
    const config = OBJECT_ATTACHMENT_CONFIG[objectType as ChatObjectAttachmentType];
    if (!config) {
        return count === 1 ? 'attachment' : 'attachments';
    }

    return count === 1 ? config.label : config.pluralLabel;
}

export function getObjectAttachmentIcon(objectType: string): string | null {
    return OBJECT_ATTACHMENT_CONFIG[objectType as ChatObjectAttachmentType]?.icon ?? null;
}

export function getObjectAttachmentName(attachment: Attachment): string {
    return attachment.object?.name?.trim() || 'Shared content';
}

export function getObjectAttachmentImageUrl(attachment: Attachment): string | null {
    return attachment.object?.image_url || null;
}

export function getObjectAttachmentMeta(attachment: Attachment): string {
    return attachment.object?.meta?.trim() || '';
}

function getFileAttachmentPluralLabel(type: string): string {
    if (type === 'GIF') {
        return 'GIFs';
    }

    return `${type}s`;
}

function getAttachmentPreviewLabel(attachment: Attachment, count: number): { label: string; icon: string | null } {
    if (isObjectAttachment(attachment)) {
        return {
            label: getObjectAttachmentLabel(attachment.object_type, count),
            icon: getObjectAttachmentIcon(attachment.object_type),
        };
    }

    const fileType = getFileAttachmentType(attachment);

    return {
        label: count === 1 ? fileType : getFileAttachmentPluralLabel(fileType),
        icon: null,
    };
}

function formatDirectAttachmentPreview(
    count: number,
    label: string,
    icon: string | null,
    isMyMessage: boolean,
    authorName: string
): LastMessageAttachmentPreview {
    const senderName = isMyMessage ? 'You' : authorName;

    if (count === 1) {
        return {
            text: `${senderName} sent a ${label}`,
            icon,
        };
    }

    return {
        text: `${senderName} sent ${count} ${label}`,
        icon,
    };
}

function formatGroupAttachmentPreview(count: number, label: string, icon: string | null): LastMessageAttachmentPreview {
    if (count === 1) {
        return { text: label, icon };
    }

    return { text: `${count} ${label}`, icon };
}

export function getLastMessageAttachmentPreview(
    lastMessage: LastMessageLike | undefined | null,
    format: 'direct' | 'group'
): LastMessageAttachmentPreview {
    if (!lastMessage) {
        return { text: '', icon: null };
    }

    if (lastMessage.message) {
        return { text: lastMessage.message, icon: null };
    }

    const attachments = lastMessage.attachments ?? [];
    if (attachments.length === 0) {
        return { text: '', icon: null };
    }

    const lastAttachment = attachments[attachments.length - 1];
    const { label, icon } = getAttachmentPreviewLabel(lastAttachment, attachments.length);

    if (format === 'direct') {
        return formatDirectAttachmentPreview(
            attachments.length,
            label,
            icon,
            !!lastMessage.is_my_message,
            lastMessage.author?.name ?? ''
        );
    }

    return formatGroupAttachmentPreview(attachments.length, label, icon);
}
